import { createAction, props } from '@ngrx/store';
import { RecipientEntryState, SavedRecipientsState } from '../recipients-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Recipients store actions.
 *
 * Payloads are restricted to the serializable `RecipientEntryState` / `SavedRecipientsState`
 * shapes (or primitives) so they pass the root store's `strictActionSerializability` check.
 * Model conversion stays in `RecipientsNgrxService`; only plain state flows through these
 * actions.
 *
 * `correlationId` on the write actions lets `recipients-write-bridge.ts` match a settle
 * action back to the specific request it dispatched, so the returned observable completes
 * (or errors) for that write rather than for a concurrent one.
 */

// --- Lifecycle ---------------------------------------------------------------

/** Dispatched by the service's `init()` to start the storage-load effect. */
export const initRecipients = createAction('[Recipients] Init');

/** Dispatched by the hydrate effect once the persisted collection has been read. */
export const recipientsLoadedFromStorage = createAction(
    '[Recipients] Loaded from storage',
    props<{ entries: SavedRecipientsState[] }>(),
);

// --- Mutations -----------------------------------------------------------------

/**
 * Dispatched by the service's `saveRecipientsByNotificationId`. The reducer creates the
 * entry if absent (via `createDefaultSavedRecipients`) and replaces its recipients —
 * this is where the legacy `findIndex` / `if (i !== -1) … else push` logic goes.
 */
export const upsertRecipientsRequested = createAction(
    '[Recipients] Save requested',
    props<{ notificationId: NotificationIdState; recipients: RecipientEntryState[]; correlationId: string }>(),
);

/**
 * Dispatched by the service's `removeRecipientsFromLocalStorage`. The reducer filters out
 * the matching entry; an unknown id is a no-op that keeps the same state reference,
 * mirroring the legacy `index === -1` no-op.
 */
export const removeRecipientsRequested = createAction(
    '[Recipients] Remove requested',
    props<{ notificationId: NotificationIdState; correlationId: string }>(),
);

// --- Settle ---------------------------------------------------------------------

/**
 * Settle-only signal that a write was persisted successfully (mapped from the generic
 * `slicePersisted`). The store is already the source of truth, so this carries no
 * collection payload — it only lets `recipients-write-bridge.ts` complete for the
 * matching `correlationId`.
 */
export const recipientsSaved = createAction('[Recipients] Saved', props<{ correlationId?: string }>());

export const recipientsSaveError = createAction(
    '[Recipients] Save error',
    props<{ error: string; correlationId?: string }>(),
);
